import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { FiUsers, FiSearch, FiRefreshCcw, FiPlus } from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Select from '../../components/Select/Select'
import Button from '../../components/Button/Button'
import './StudentProfiles.css'

const StudentProfiles = () => {
  const [profiles, setProfiles] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadProfiles()
  }, [page, statusFilter])

  const loadProfiles = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page,
        limit: 25,
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }
      const response = await adminApi.get('/student-profiles', { params })
      setProfiles(response.data.data || response.data || [])
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student profiles')
      console.error('Error loading student profiles:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const filteredProfiles = useMemo(() => {
    const profilesList = profiles || []
    if (!Array.isArray(profilesList)) return []
    const query = searchQuery.toLowerCase()
    return profilesList.filter((profile) => {
      const matchesSearch =
        !query ||
        [
          profile.fullName,
          profile.email,
          profile.phone,
          profile.batch,
          profile.cohort,
          ...(profile.skills || []),
        ]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query))
      
      const matchesStatus = statusFilter === 'all' || profile.currentStatus === statusFilter
      
      return matchesSearch && matchesStatus
    })
  }, [profiles, searchQuery, statusFilter])

  const columns = [
    {
      key: 'fullName',
      label: 'Name',
      render: (row) => (
        <div>
          <strong>{row.fullName}</strong>
          <span className="student-profile-email">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'phone',
      label: 'Contact',
      render: (row) => row.phone || '—',
    },
    {
      key: 'batch',
      label: 'Batch/Cohort',
      render: (row) => (
        <div>
          {row.batch && <div>{row.batch}</div>}
          {row.cohort && <div className="student-profile-cohort">{row.cohort}</div>}
          {!row.batch && !row.cohort && '—'}
        </div>
      ),
    },
    {
      key: 'skills',
      label: 'Skills',
      render: (row) => (
        <div className="student-profile-skills">
          {row.skills?.slice(0, 3).join(', ') || '—'}
          {row.skills?.length > 3 && '...'}
        </div>
      ),
    },
    {
      key: 'currentStatus',
      label: 'Status',
      render: (row) => <StatusBadge status={row.currentStatus || 'available'} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link to={`/student-profiles/${row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ]

  return (
    <div className="student-profiles-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Placement Management</p>
          <h1>Student Profiles</h1>
          <p className="subhead">Manage student profiles for placement</p>
        </div>
        <div className="header-actions">
          <Button
            onClick={() => window.location.href = '/student-profiles/new'}
          >
            <FiPlus size={18} />
            Create Profile
          </Button>
          <Button
            variant="secondary"
            onClick={loadProfiles}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </Button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search student profiles"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'available', label: 'Available' },
            { value: 'placed', label: 'Placed' },
            { value: 'on-hold', label: 'On Hold' },
            { value: 'not-interested', label: 'Not Interested' },
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredProfiles}
        loading={isLoading}
        emptyMessage="No student profiles found"
      />
    </div>
  )
}

export default StudentProfiles

